import { useMemo } from "react"
import { get, isEmpty, compact, isFunction, chain, isNil } from "lodash"
import { usePositionSetMetadata } from "resources/position-sets"
import { INSTRUMENTS, INSTRUMENTS_BY_MODE } from "connected-components/dashboard/helpers"
import { DELTA_SYMBOL } from "helpers/assets/constants"
import { unwrapValue } from "helpers/assets/unwrapValue"
import { VALUE_FORMATS } from "helpers/metrics/constants"
import { getExcelValue } from "helpers/csv"
import { buildContributorKey } from "helpers/contributors"
import { OK } from "helpers/data-statuses"

export function getContributorLabel(contributor) {
  const tickerPart = contributor.assetClass === "equity"
    ? contributor.id.split(".")[0]
    : contributor.id

  if (!tickerPart) {
    return "Unknown"
  }

  const countryPart = contributor.country && contributor.country !== "US"
    ? ` ${contributor.country}`
    : ""

  return tickerPart + countryPart
}

// [TODO] explore the possibility of using Symbol. Does weird things with for..in loops.
export const NOT_HELD = "notheld"

function getBetaInstrumentType(instrument) {
  if (instrument.type) {
    return instrument.type
  }
  if (instrument.securityId) {
    return "ETF"
  }
  if (instrument.resource) {
    return instrument.resource.type
  }
  if (instrument.benchmarkId) {
    return "BENCHMARK"
  }
  if (instrument.indexId) {
    return "INDEX"
  }
  return null
}

export function usePositionSetBetaInstrument(positionSetMetadata = {}, modelMetadata = {}) {
  const psBetaInstrument = useMemo(() => {
    if (!positionSetMetadata.betaInstrument) {
      return null
    }
    const instrument = positionSetMetadata.betaInstrument?.resource || positionSetMetadata.betaInstrument
    const type = getBetaInstrumentType(instrument)
    switch (type) {
      case "INDEX":
        return { ...instrument, id: instrument.indexId, type }
      case "ETF":
        return { ...instrument, ...instrument.securityId, id: instrument.securityId.internalId, type }
      case "BENCHMARK":
        return { ...instrument, id: instrument.benchmarkId, type }
      default:
        return { ...instrument, id: instrument.alias, type }
    }
  }, [positionSetMetadata])

  const date = modelMetadata?.date || modelMetadata?.availability?.currentDate
  // TODO [APP-2592] - remove the 4th parameter once corrected
  const [betaInstrument, betaInstrumentStatus] = usePositionSetMetadata(modelMetadata?.id, psBetaInstrument, date, "beta-instrument")
  return useMemo(() => {
    if (betaInstrumentStatus !== OK || !betaInstrument) {
      return null
    }

    return {
      ...psBetaInstrument,
      ...betaInstrument,
      description: betaInstrument.description || betaInstrument.name,
    }
  }, [betaInstrumentStatus, betaInstrument, psBetaInstrument])
}

export function getGatherersFromInstruments(availableInstruments, handleGetInstrumentGatherers) {
  return Object.entries(availableInstruments).reduce((acc, [instrumentType, instrument]) => {
    const suffix = INSTRUMENTS[instrumentType].metricsSuffix
    return [...acc, ...handleGetInstrumentGatherers(instrument, suffix, instrumentType)]
  }, [])
}

// This regex is used to strip the suffix from the metric id
// For example, if the metric id is "risk-tertiary.riskDecomposition", the suffix is "-tertiary"
// So the stripped id is "risk.riskDecomposition"
const idSuffixesRegex = chain(INSTRUMENTS)
  .map(({ suffix }) => suffix)
  .compact()
  .join("|")
  .thru((regex) => new RegExp(`${regex}$`))
  .value()

export function getStrippedId(metric) {
  return (metric.id || "").replace(idSuffixesRegex, "")
}

function shouldShowDownloadMetric(metric, existingMetrics) {
  const key = getDownloadDedupKey(metric)
  if (!key) {
    return true
  }

  return !existingMetrics.has(key)
}

function makeDownloadHeaders(metrics) {
  // If a particular metric or header should only be shown once in the download, even across multiple instruments,
  // we need to keep track of the root metric/header and skip its subsequent variants.
  const seenMetrics = {}
  return metrics.reduce((downloadHeaders, metric) => {
    const key = getDownloadDedupKey(metric)
    if (!key || !seenMetrics[key]) {
      downloadHeaders.push(metric.downloadLabel || metric.label)
    }
    if (key) {
      seenMetrics[key] = true
    }
    return downloadHeaders
  }, [])
}

export function getMetricValue(metric, item) {
  let value
  if (isFunction(metric.dataGetter)) {
    value = metric.dataGetter(item.values, { dataPath: metric.dataPath, label: item.label })
  } else if (isFunction(metric.downloadDataGetter)) {
    value = metric.downloadDataGetter(item.values, { dataPath: metric.dataPath, label: item.label })
  } else if (metric.dataPath) {
    value = get(item.values, metric.dataPath)
  } else {
    value = get(item, metric.id)
  }
  return unwrapValue(value)
}

export function formatMetricValue(value, metric) {
  if (value === "" || isNil(value)) {
    return ""
  }
  const format = metric.format || metric.outputFormat
  const isPercent = format === VALUE_FORMATS.PERCENT || format === VALUE_FORMATS.PERCENT_100
  const adjustedValue = format === VALUE_FORMATS.PERCENT_100 ? value / 100 : value
  return isNaN(adjustedValue) ? adjustedValue : getExcelValue(adjustedValue, { percent: isPercent })
}

function getDownloadDedupKey(metric) {
  return metric.onlyShowOnceInDownload ? getStrippedId(metric) : null
}

function makeDeduplicatedMetricRow(metrics, position) {
  const seenMetricIds = new Set()
  return chain(metrics)
    .map(metric => {
      if (!shouldShowDownloadMetric(metric, seenMetricIds)) {
        return null
      }
      const key = getDownloadDedupKey(metric)
      if (key) {
        seenMetricIds.add(key)
      }
      const value = getMetricValue(metric, position)
      return formatMetricValue(value, metric)
    })
    .filter(value => {
      return !isNil(value)
    })
    .value()
}

function makeDownloadRows(data, metrics, shouldFlatten) {
  if (isEmpty(data)) {
    return []
  }

  const flattenedLeafIds = new Set()

  return data.flatMap((item) => {
    if (shouldFlatten && item.children) {
      return Object.values(item.children).reduce((acc, position) => {
        if (position.children) {
          return acc
        }

        if (position.id) {
          flattenedLeafIds.add(position.id)
        }

        acc.push(makeDeduplicatedMetricRow(metrics, position))
        return acc
      }, [])
    }

    if (shouldFlatten && item.id && flattenedLeafIds.has(item.id)) {
      return []
    }

    return [makeDeduplicatedMetricRow(metrics, item)]
  })
}

export function makeAssetViewDownloadData(values) {
  if (isEmpty(values)) {
    return null
  }
  const { data: metricData, metrics, shouldFlatten } = values
  return {
    data: {
      headers: makeDownloadHeaders(metrics),
      rows: compact(makeDownloadRows(metricData, metrics, shouldFlatten)),
    },
  }
}

// marginal and marginalContribution are used interchangeably based on OP templates vs custom metrics
const METRICS_WITHOUT_MAGNITUDE_ORDERING = ["marginal", "marginalContribution", "beta", "summary"]

export const COLUMN_ORDER_OPTIONS_ID = {
  CUSTOM: "custom",
  DEFAULT: "default",
}

export function useColumnOrderOptions(selectedView, selectedMode) {
  return useMemo(() => {
    if (METRICS_WITHOUT_MAGNITUDE_ORDERING.includes(selectedView)) {
      return { options: null, order: null }
    }
    const canOrderByMagnitude = selectedView !== "custom"

    const instruments = INSTRUMENTS_BY_MODE[selectedMode]
    let magnitudeId = instruments[0]
    let magnitudeLabel = "Biggest Contributor"
    if (instruments.length > 1) {
      magnitudeId = "delta"
      magnitudeLabel = `Biggest Contributor (${DELTA_SYMBOL})`
    }

    const options = compact([{
      id: COLUMN_ORDER_OPTIONS_ID.DEFAULT,
      primaryText: "Default",
      secondaryText: "The view's default ordering",
    }, canOrderByMagnitude && {
      id: magnitudeId,
      primaryText: magnitudeLabel,
      secondaryText: "Ordered high to low by contribution to this instrument",
    }, {
      id: COLUMN_ORDER_OPTIONS_ID.CUSTOM,
      primaryText: "Custom",
      secondaryText: "Your saved column order from drag and drop",
    }])

    return { options, order: canOrderByMagnitude ? magnitudeId : "default" }
  }, [selectedView, selectedMode])
}

// The rebalancedInstrument here is used to prevent showing trade highlights before the rebalance is applied
// When a trade is added, the query for the rebalancedInstrument are fired and the rebalancedInstrument is set to null
export function useHighlightedTradeRowIds(trades, rebalancedInstrument) {
  return useMemo(() => {
    if (!rebalancedInstrument) {
      return []
    }
    return trades.map(trade => {
      // Trades don't have modelProviderId, but the contributor id depends on it for indexes
      const security = {
        ...trade.security,
        ...(trade.security.assetClass === "index" && { modelProviderId: `i.${trade.security.id}` }),
      }
      return buildContributorKey(security)
    })
  }, [trades, rebalancedInstrument])
}
